/**
 * TerritoryLayer
 *
 * Renders pre-aggregated territory grid cells as filled polygons at zoom levels
 * below 12, where individual tiles are too small to see.
 *
 * Visual zoom transitions:
 *   zoom 0–7:    coarse cells at full opacity; fine cells hidden
 *   zoom 7–8:    coarse cells fade out; fine cells fade in
 *   zoom 8–13:   fine cells at full opacity; coarse cells hidden
 *   zoom 13–15:  fine cells fade out (tile fills become visible at zoom 15)
 *   zoom >= 15:  territory fully hidden; individual tile fills take over
 *
 * Rationale: game tiles are ~0.2px at zoom 12 and only exceed 1px at zoom ~15.
 * Showing cells up to zoom 15 ensures the map always has visible territory data.
 *
 * Z-ORDER (critical): TerritoryLayer MUST be mounted BEFORE TilesLayer in MapView.tsx.
 * GeoJsonLayer calls map.addLayer() at mount time, so JSX order controls Mapbox layer
 * stack order. Territory must sit below tile outlines/fills at high zoom.
 *
 * Each resolution uses two GeoJsonLayer instances sharing one source (fill + outline),
 * matching the [geojsonlayer-duplicate-source-on-split] pattern from TilesLayer.
 */

import { useEffect, useMemo, useRef } from 'react';
import { Position } from 'geojson';
import { useAppSelector } from '../../../redux/hooks';
import { selectFineCells, selectCoarseCells, GridCell } from '../../../redux/features/firestore/cells/slice';
import { MapState, MapBounds, selectMapState } from '../../../redux/features/map/slice';
import { boundsContainsBounds, inflateBounds } from '../../../redux/features/map/tools';
import { CELL_RESOLUTION_FINE, CELL_RESOLUTION_COARSE } from '@painttheworld/shared';
import GridCellsProvider from '../../../redux/features/firestore/cells/GridCellsProvider';
import GeoJsonLayer from './GeoJsonLayer';
import MapElementProps from '../MapElementProps';

// ─── Visual constants ─────────────────────────────────────────────────────────
// Single source of truth for all zoom transition behavior.
// Opacity values calibrated for Mapbox Streets style (light/grey background).
const TERRITORY_FILL_OPACITY    = 0.5;  // fill alpha — transparent enough to see map underneath
const TERRITORY_OUTLINE_OPACITY = 0.7;  // outline alpha — higher to anchor light colors (yellow, white)
const ZOOM_COARSE_HIDE          = 8;    // zoom at which coarse cells become fully hidden
const ZOOM_FINE_SHOW            = 8;    // zoom at which fine cells start fading in
const ZOOM_FINE_FULL            = 9;    // zoom at which fine cells reach full opacity
const ZOOM_FINE_FADE_START      = 13;   // zoom at which fine cells start fading out
const ZOOM_FINE_HIDE            = 15;   // zoom at which fine cells are fully hidden

// Minimum ms between Firestore resubscriptions when the viewport moves.
const REFETCH_DEBOUNCE_MS = 2000;
// How far outside the current viewport before we re-query (same as TilesLayer).
const REFETCH_BUFFER_METERS = 5000;

/**
 * Compute the MapBounds for a cell from its cellId and resolution.
 * Bounds are not stored in Firestore — derived deterministically client-side.
 */
function cellIdToBounds(cellId: string, resolution: number): MapBounds {
    const [lngStr, latStr] = cellId.split(':');
    const centerLng = parseFloat(lngStr);
    const centerLat = parseFloat(latStr);
    return {
        north: centerLat + resolution / 2,
        south: centerLat - resolution / 2,
        east:  centerLng + resolution / 2,
        west:  centerLng - resolution / 2,
    };
}

/**
 * Build a GeoJSON FeatureCollection from cells, grouping by dominantColor.
 * Each color becomes one MultiPolygon feature (same pattern as TilesLayer).
 */
function buildGeoJson(
    cells: { [cellId: string]: GridCell },
    resolution: number,
): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const cellsByColor: { [color: string]: GridCell[] } = {};

    for (const cell of Object.values(cells)) {
        const color = cell.dominantColor;
        if (!(color in cellsByColor)) {
            cellsByColor[color] = [];
        }
        cellsByColor[color].push(cell);
    }

    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];

    for (const color in cellsByColor) {
        const coordinates: Position[][][] = [];
        for (const cell of cellsByColor[color]) {
            const b = cellIdToBounds(cell.cellId, resolution);
            coordinates.push([[
                [b.east,  b.north],
                [b.east,  b.south],
                [b.west,  b.south],
                [b.west,  b.north],
                [b.east,  b.north],
            ]]);
        }
        features.push({
            type: 'Feature',
            geometry: { type: 'MultiPolygon', coordinates },
            properties: { fill: color, stroke: color, 'stroke-width': 0.1 },
        });
    }

    return { type: 'FeatureCollection', features };
}

// ─── Component ────────────────────────────────────────────────────────────────

const TerritoryLayer = ({ map }: MapElementProps) => {
    const fineCells   = useAppSelector(selectFineCells);
    const coarseCells = useAppSelector(selectCoarseCells);
    const mapState: MapState = useAppSelector(selectMapState);

    const lastBounds = useRef<MapBounds>();
    const mapStateRef = useRef(mapState);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    mapStateRef.current = mapState;

    const fetchCells = (state: MapState) => {
        if (!map) return;
        const mapBounds = map.getBounds();
        const liveBounds: MapBounds = {
            north: mapBounds.getNorth(),
            south: mapBounds.getSouth(),
            east:  mapBounds.getEast(),
            west:  mapBounds.getWest(),
        };
        lastBounds.current = inflateBounds(liveBounds, REFETCH_BUFFER_METERS);

        GridCellsProvider.instance.getCells(state, () => map.getBounds());
    };

    useEffect(() => {
        const { latitude, longitude } = mapState.locationAndZoom.location;
        if (latitude === 0 && longitude === 0) return;

        // First real location: fetch immediately
        if (!lastBounds.current) {
            fetchCells(mapState);
            return;
        }

        // Viewport moved outside buffered area: debounce
        if (map) {
            const mb = map.getBounds();
            const currentBounds: MapBounds = {
                north: mb.getNorth(), south: mb.getSouth(),
                east: mb.getEast(), west: mb.getWest(),
            };

            if (!boundsContainsBounds(lastBounds.current, currentBounds)) {
                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                debounceTimer.current = setTimeout(() => {
                    debounceTimer.current = null;
                    fetchCells(mapStateRef.current);
                }, REFETCH_DEBOUNCE_MS);
            }
        }
    }, [mapState]); // eslint-disable-line

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            lastBounds.current = undefined;
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            GridCellsProvider.instance.disconnect();
        };
    }, []);

    // Memoize per cell set — an unmemoized rebuild hands a new object to each
    // GeoJsonLayer every render, forcing a full re-ship of the geometry to
    // Mapbox on the 100ms redraw loop even when nothing changed.
    const coarseData = useMemo(() => buildGeoJson(coarseCells, CELL_RESOLUTION_COARSE), [coarseCells]);
    const fineData   = useMemo(() => buildGeoJson(fineCells,   CELL_RESOLUTION_FINE),   [fineCells]);

    return (
        <>
            {/* ── Coarse cells (0.1°, ~11km, zoom 0–8) ───────────────────── */}
            {/* Fill layer */}
            <GeoJsonLayer map={map} data={coarseData} sourceId="territory_coarse_source" layer={{
                id: 'TerritoryLayerCoarseFill',
                type: 'fill',
                layout: {},
                paint: {
                    'fill-color': ['get', 'fill'],
                    // Fade out as zoom approaches ZOOM_COARSE_HIDE
                    'fill-opacity': [
                        'interpolate', ['linear'], ['zoom'],
                        ZOOM_COARSE_HIDE - 1, TERRITORY_FILL_OPACITY,
                        ZOOM_COARSE_HIDE, 0,
                    ] as any,
                },
            }} />
            {/* Outline layer — higher opacity than fill for legibility of light colors */}
            <GeoJsonLayer map={map} data={coarseData} sourceId="territory_coarse_source" layer={{
                id: 'TerritoryLayerCoarseOutline',
                type: 'line',
                layout: {},
                paint: {
                    'line-color': ['get', 'fill'],
                    'line-opacity': [
                        'interpolate', ['linear'], ['zoom'],
                        ZOOM_COARSE_HIDE - 1, TERRITORY_OUTLINE_OPACITY,
                        ZOOM_COARSE_HIDE, 0,
                    ] as any,
                    'line-width': 1,
                },
            }} />

            {/* ── Fine cells (0.01°, ~1.1km, zoom 8–15) ──────────────────── */}
            {/* Fill layer */}
            <GeoJsonLayer map={map} data={fineData} sourceId="territory_fine_source" layer={{
                id: 'TerritoryLayerFineFill',
                type: 'fill',
                layout: {},
                paint: {
                    'fill-color': ['get', 'fill'],
                    // Fade in at zoom 8, hold, then fade out at zoom 15
                    'fill-opacity': [
                        'interpolate', ['linear'], ['zoom'],
                        ZOOM_FINE_SHOW, 0,
                        ZOOM_FINE_FULL, TERRITORY_FILL_OPACITY,
                        ZOOM_FINE_FADE_START, TERRITORY_FILL_OPACITY,
                        ZOOM_FINE_HIDE, 0,
                    ] as any,
                },
            }} />
            {/* Outline layer */}
            <GeoJsonLayer map={map} data={fineData} sourceId="territory_fine_source" layer={{
                id: 'TerritoryLayerFineOutline',
                type: 'line',
                layout: {},
                paint: {
                    'line-color': ['get', 'fill'],
                    'line-opacity': [
                        'interpolate', ['linear'], ['zoom'],
                        ZOOM_FINE_SHOW, 0,
                        ZOOM_FINE_FULL, TERRITORY_OUTLINE_OPACITY,
                        ZOOM_FINE_FADE_START, TERRITORY_OUTLINE_OPACITY,
                        ZOOM_FINE_HIDE, 0,
                    ] as any,
                    'line-width': 1,
                },
            }} />
        </>
    );
};

export default TerritoryLayer;
